import React, { createContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { authService } from '../services/auth.service';

export interface TeacherProfile {
  avatar?: string;
  location?: string;
  experience?: string;
  subjects?: string[];
  curricula?: string[];
  bio?: string;
  availabilitySlots?: string[];
  upiId?: string;
  upiNumber?: string;
  teachingCourseCertificate?: string;
  aadhaarMasked?: string;
  aadhaarKycStatus?: 'NOT_STARTED' | 'OTP_SENT' | 'VERIFIED' | 'FAILED';
  aadhaarKycUpdatedAt?: string;
  aadhaarKycVerifiedAt?: string;
  qualifications?: Array<{
    level: 'SCHOOL' | 'INTERMEDIATE' | 'DIPLOMA' | 'UG' | 'PG' | 'DOCTORATE' | 'CERTIFICATION' | 'OTHER';
    course: string;
    specialization?: string;
    institute?: string;
    yearOfCompletion?: string;
  }>;
  profileCompletionPercentage?: number;
  profileComplete?: boolean;
  certificationPassed?: boolean;
  isReadyForStudentVisibility?: boolean;
  leadsEnabled?: boolean;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED';
}

export interface User {
  id: string;
  email?: string;
  avatar?: string;
  username?: string;
  phone?: string;
  referralCode?: string;
  referralCount?: number;
  referralBonusEarned?: number;
  firstName: string;
  lastName: string;
  role: 'TEACHER' | 'STUDENT' | 'SELLER' | 'SPORTS' | 'RIDES' | 'ADMIN';
  sportsRoles?: string[];
  ridesRoles?: string[];
  isPhoneVerified?: boolean;
  isActive?: boolean;
  accountStatus?: 'ACTIVE' | 'DELETED' | 'BLOCKED' | 'PENDING_VERIFICATION';
  studentRegistrationMode?: 'SELF' | 'CHILD';
  studentDisplayFirstName?: string;
  studentDisplayLastName?: string;
  studentRelationToUser?: string;
  accessToken: string;
  teacherProfile?: TeacherProfile;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<User>;
  requestRestoreAccountOtp: (identifier: string, password: string) => Promise<any>;
  restoreDeletedAccount: (userId: string, otp: string) => Promise<User>;
  register: (userData: any) => Promise<any>;
  verifyRegistrationOtp: (identifier: string, otp: string) => Promise<User>;
  resendRegistrationOtp: (identifier: string) => Promise<any>;
  requestSportsAccountSwitchOtp: (identifier: string) => Promise<any>;
  verifySportsAccountSwitchOtp: (data: any) => Promise<User>;
  requestRidesAccountSwitchOtp: (identifier: string) => Promise<any>;
  verifyRidesAccountSwitchOtp: (data: any) => Promise<User>;
  requestForgotPasswordOtp: (identifier: string) => Promise<any>;
  resetPasswordWithOtp: (identifier: string, otp: string, newPassword: string) => Promise<any>;
  logout: () => void;
  updateTeacherProfile: (profileData: TeacherProfile) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const normalizeAuthUser = (rawUser: any): User | null => {
  if (!rawUser) return null;
  const userPayload = rawUser.user ? rawUser.user : rawUser;
  const resolvedId =
    userPayload?.id ||
    userPayload?._id ||
    userPayload?.sub ||
    rawUser?.id ||
    rawUser?._id ||
    rawUser?.sub;
  if (!resolvedId || !userPayload?.role) return null;

  return {
    ...userPayload,
    id: String(resolvedId),
    accessToken: rawUser.accessToken || userPayload.accessToken || '',
  } as User;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = authService.getCurrentUser();
    const normalizedUser = normalizeAuthUser(storedUser);
    if (normalizedUser) {
      setUser(normalizedUser);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (identifier: string, password: string) => {
    try {
      const credentials = { identifier, password };
      const userData = await authService.login(credentials);
      const normalizedUser = normalizeAuthUser(userData);
      if (!normalizedUser) {
        throw new Error('Invalid user response shape');
      }
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      setUser(normalizedUser);
      return normalizedUser;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, []);

  const register = useCallback(async (userData: any) => {
    try {
      const response = await authService.register(userData);
      return response.data;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  }, []);

  const verifyRegistrationOtp = useCallback(async (identifier: string, otp: string) => {
    try {
      const response = await authService.verifyRegistrationOtp(identifier, otp);
      const normalizedUser = normalizeAuthUser(response.data);
      if (!normalizedUser) {
        throw new Error('Invalid user response shape');
      }
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      setUser(normalizedUser);
      return normalizedUser;
    } catch (error) {
      console.error('OTP verification failed:', error);
      throw error;
    }
  }, []);

  const resendRegistrationOtp = useCallback(async (identifier: string) => {
    const response = await authService.resendRegistrationOtp(identifier);
    return response.data;
  }, []);

  const requestRestoreAccountOtp = useCallback(async (identifier: string, password: string) => {
    return authService.requestRestoreAccountOtp({ identifier, password });
  }, []);

  const requestSportsAccountSwitchOtp = useCallback(async (identifier: string) => {
    const response = await authService.requestSportsAccountSwitchOtp(identifier);
    return response.data;
  }, []);

  const verifySportsAccountSwitchOtp = useCallback(async (data: any) => {
    try {
      const response = await authService.verifySportsAccountSwitchOtp(data);
      const normalizedUser = normalizeAuthUser(response.data);
      if (!normalizedUser) {
        throw new Error('Invalid user response shape');
      }
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      setUser(normalizedUser);
      return normalizedUser;
    } catch (error) {
      console.error('Sports account switch failed:', error);
      throw error;
    }
  }, []);

  const restoreDeletedAccount = useCallback(async (userId: string, otp: string) => {
    try {
      const userData = await authService.restoreDeletedAccount({
        userId,
        otp,
      });
      const normalizedUser = normalizeAuthUser(userData);
      if (!normalizedUser) {
        throw new Error('Invalid user response shape');
      }
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      setUser(normalizedUser);
      return normalizedUser;
    } catch (error) {
      console.error('Account restore failed:', error);
      throw error;
    }
  }, []);

  const requestRidesAccountSwitchOtp = useCallback(async (identifier: string) => {
    const response = await authService.requestRidesAccountSwitchOtp(identifier);
    return response.data;
  }, []);

  const verifyRidesAccountSwitchOtp = useCallback(async (data: any) => {
    try {
      const response = await authService.verifyRidesAccountSwitchOtp(data);
      const normalizedUser = normalizeAuthUser(response.data);
      if (!normalizedUser) {
        throw new Error('Invalid user response shape');
      }
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      setUser(normalizedUser);
      return normalizedUser;
    } catch (error) {
      console.error('Rides account switch failed:', error);
      throw error;
    }
  }, []);

  const requestForgotPasswordOtp = useCallback(async (identifier: string) => {
    const response = await authService.requestForgotPasswordOtp(identifier);
    return response.data;
  }, []);

  const resetPasswordWithOtp = useCallback(async (identifier: string, otp: string, newPassword: string) => {
    const response = await authService.resetPasswordWithOtp(identifier, otp, newPassword);
    return response.data;
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const updateTeacherProfile = useCallback(async (profileData: TeacherProfile) => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      const updatedUser = await authService.updateTeacherProfile(profileData);
      setUser(updatedUser);
    } catch (error) {
      console.error('Failed to update teacher profile:', error);
      throw error;
    }
  }, [user]);

  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    requestRestoreAccountOtp,
    restoreDeletedAccount,
    register,
    verifyRegistrationOtp,
    resendRegistrationOtp,
    requestSportsAccountSwitchOtp,
    verifySportsAccountSwitchOtp,
    requestRidesAccountSwitchOtp,
    verifyRidesAccountSwitchOtp,
    requestForgotPasswordOtp,
    resetPasswordWithOtp,
    logout,
    updateTeacherProfile,
  }), [
    user,
    loading,
    login,
    requestRestoreAccountOtp,
    restoreDeletedAccount,
    register,
    verifyRegistrationOtp,
    resendRegistrationOtp,
    requestSportsAccountSwitchOtp,
    verifySportsAccountSwitchOtp,
    requestRidesAccountSwitchOtp,
    verifyRidesAccountSwitchOtp,
    requestForgotPasswordOtp,
    resetPasswordWithOtp,
    logout,
    updateTeacherProfile,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
